import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

const ANOMALY_COLORS = {
  "Late":            "#fbbf24",
  "Missing Punch":   "#f87171",
  "Missing Out":     "#f87171",
  "Short Hours":     "#fb923c",
  "No Show":         "#f43f5e",
  "Overtime":        "#60a5fa",
  "Early Departure": "#c084fc",
};

function DeltaIcon({ delta }) {
  if (delta > 0)  return <TrendingUp  size={12} className="text-red-400"    />;
  if (delta < 0)  return <TrendingDown size={12} className="text-[#00ff90]" />;
  return <Minus size={12} className="text-white/20" />;
}

export default function AnomaliesPanel({ loading, rows }) {
  if (loading) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Anomalies</div>
      <div className="h-48 rounded-lg bg-white/5 animate-pulse" />
    </div>
  );

  const total = rows.reduce((s, r) => s + (r.count || 0), 0);

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Anomalies</span>
        {total > 0 && <span className="text-sm font-bold text-[#fbbf24]">{total} total</span>}
      </div>
      <div className="p-5 space-y-4">
        {rows.length === 0 ? (
          <p className="text-white/20 text-sm italic text-center py-4">No anomalies in this period.</p>
        ) : (
          <>
            {/* Bar chart */}
            <ResponsiveContainer width="100%" height={100}>
              <BarChart data={rows} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.35)" }} />
                <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} allowDecimals={false} />
                <Tooltip {...tooltipStyle} formatter={v => [v, "Count"]} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {rows.map((r, i) => (
                    <Cell key={i} fill={ANOMALY_COLORS[r.label] || "#ffffff33"} fillOpacity={0.8} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Stat cards */}
            <div className="grid grid-cols-2 gap-2">
              {rows.map(a => {
                const color = ANOMALY_COLORS[a.label] || "#ffffff44";
                return (
                  <div key={a.id || a.label} className="rounded-lg border border-white/5 bg-white/3 p-3">
                    <p className="text-xs text-white/40 mb-1">{a.label}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold" style={{ color }}>{a.count}</span>
                      <div className="flex items-center gap-1">
                        <DeltaIcon delta={a.delta} />
                        {a.delta !== 0 && a.delta != null && (
                          <span className="text-xs" style={{ color: a.delta > 0 ? "#f87171" : "#00ff90" }}>
                            {a.delta > 0 ? `+${a.delta}` : a.delta}
                          </span>
                        )}
                      </div>
                    </div>
                    {a.dept && <p className="text-xs text-white/20 mt-0.5">{a.dept}</p>}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}